import { useQueries } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { getInstanceDirectory } from '../../portal/instanceDirectory';
import { useInstances } from '../useInstances';
import { instanceQueryKey } from './useInstanceLabel';
import { usePortalAccess } from './usePortalAccess';

const STALE_TIME = 5 * 60 * 1000;

/**
 * Is this instance id one the caller may actually use?
 *
 * The source is the portal only when the feature is on *and* a portal host is configured
 * (`isPortalSource`) — not `isEnabled` alone. Flag-on-with-no-host must fall back to the
 * guardian list, the same as flag-off: otherwise every portal query stays permanently
 * disabled, no candidate id ever resolves, and every `?instance_ids=` deep link is
 * silently stripped.
 *
 * With the portal as the source, an id whose lookup hasn't settled yet counts as known —
 * it settles to found-or-not-found once the query resolves. This mirrors the label
 * resolver, which shows the raw id while unresolved rather than hiding it. A **settled
 * and absent** id is still not known — that's the real out-of-scope case. A **failed**
 * lookup counts every candidate as known: dropping ids on a portal blip would silently
 * strip `?instance_ids=` out of someone's deep link, which is far worse than briefly
 * honouring an id the user may not own — the backend authorizes every read anyway.
 */
export const useKnownInstanceIds = (candidateIds: string[]) => {
  const { baseUrl, token, canQuery, isPortalSource } = usePortalAccess();

  const {
    data: { instances: guardianInstances },
    isFetched: isGuardianFetched,
  } = useInstances({
    enabled: !isPortalSource,
  });

  // Deduped + sorted so the query list is stable across renders that reorder the input.
  const ids = useMemo(() => [...new Set(candidateIds.filter(Boolean))].sort(), [candidateIds]);

  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: instanceQueryKey(baseUrl, id),
      queryFn: async () => (await getInstanceDirectory(baseUrl, token).load(id)) ?? null,
      enabled: canQuery,
      staleTime: STALE_TIME,
      retry: false,
    })),
  });

  const byId = useMemo(() => {
    const map = new Map<string, { isSettled: boolean; isFound: boolean }>();
    ids.forEach((id, index) => {
      const result = results[index];
      map.set(id, {
        isSettled: !!result?.isSuccess || !!result?.isError,
        // An error means "we don't know" — treat it as found rather than revoke access.
        // Unsettled (still pending) also counts as found; see the doc comment above.
        isFound: !!result?.isError || !!result?.data || !result?.isSuccess,
      });
    });
    return map;
  }, [ids, results]);

  const isKnown = useCallback(
    (id: string) => {
      if (!isPortalSource) return guardianInstances.includes(id);
      const entry = byId.get(id);
      // No entry means the id wasn't part of this hook's candidate list — not known.
      return entry ? entry.isFound : false;
    },
    [isPortalSource, guardianInstances, byId],
  );

  const isReady = useMemo(() => {
    if (!isPortalSource) return isGuardianFetched;
    // Nothing to look up is trivially ready — a `?action=` URL carrying no instance_ids
    // must not wait on a request that will never happen.
    if (ids.length === 0) return true;
    if (!canQuery) return true;
    return ids.every((id) => byId.get(id)?.isSettled);
  }, [isPortalSource, isGuardianFetched, ids, canQuery, byId]);

  return { isKnown, isReady };
};
